/**
 * Resolve a CanonicalMeasurement's reference_ranges blob to the band set
 * that applies to one patient.
 *
 * Mirrors `codings/reference_ranges.py` on the backend so the PDF and the
 * worksheet grade a value identically. Keep the two in sync.
 *
 * Two blob shapes are supported. The current fixture (version 2) is an
 * ordered rule list, first matching `when` wins; `when` may constrain `sex`
 * and/or `age` (whole years at the study date, half-open bounds):
 *
 *    { rules: [
 *        { when: { sex: 'male', age: { gte: 60 } }, bands: {...} },
 *        { when: { sex: 'male' },                   bands: {...} },
 *        { when: {},                                bands: {...} },
 *    ] }
 *
 * The legacy shape is a sex-keyed dict of band sets: `{ male: {...}, female: {...} }`.
 *
 * Age refines a range, it never withholds one: when no rule covers the
 * patient's age (no date of birth on record, or younger than the first
 * block) the age conditions are dropped and the first rule for the
 * patient's sex applies — the 20-39 baseline block, the same one the parser
 * copies into blank blocks. `resolveRange` reports that fallback so the UI
 * can say so. Sex is never relaxed: a sex-specific key still resolves
 * nothing for a patient of unknown sex.
 *
 * A `when` carrying a key this resolver does not understand, or an age
 * condition with an unknown operator or a non-numeric bound, never matches
 * (fail closed) rather than silently applying to everyone.
 */

export interface SeverityBand {
  operator?: 'between' | 'gt' | 'gte' | 'lt' | 'lte' | string;
  value?: number | number[];
}

export type SeverityRanges = Record<string, SeverityBand | null | undefined>;

export interface AgeCondition {
  gte?: number;
  gt?: number;
  lt?: number;
  lte?: number;
}

export interface RangeRule {
  when?: { sex?: string; age?: AgeCondition } | null;
  bands?: SeverityRanges | null;
}

export interface ReferenceRangesV2 {
  source?: unknown;
  rules: RangeRule[];
}

export type LegacyReferenceRanges = Record<
  string,
  SeverityRanges | null | undefined
>;

export type ReferenceRanges = ReferenceRangesV2 | LegacyReferenceRanges;

const SEX_KEY_BY_CHOICE: Record<string, string> = {
  MALE: 'male',
  FEMALE: 'female',
  M: 'male',
  F: 'female',
};

export function normalizeSex(sex: string | null | undefined): string | null {
  if (!sex) return null;
  return SEX_KEY_BY_CHOICE[sex.toUpperCase()] ?? sex.toLowerCase();
}

export function isRuleList(
  referenceRanges: ReferenceRanges
): referenceRanges is ReferenceRangesV2 {
  return Array.isArray((referenceRanges as ReferenceRangesV2).rules);
}

const AGE_OPERATORS: ReadonlySet<string> = new Set(['gte', 'gt', 'lt', 'lte']);
const WHEN_KEYS: ReadonlySet<string> = new Set(['sex', 'age']);

function isValidAgeCondition(condition: unknown): condition is AgeCondition {
  if (!condition || typeof condition !== 'object' || Array.isArray(condition))
    return false;
  const entries = Object.entries(condition);
  return (
    entries.length > 0 &&
    entries.every(
      ([op, bound]) =>
        AGE_OPERATORS.has(op) &&
        typeof bound === 'number' &&
        Number.isFinite(bound)
    )
  );
}

function ageMatches(
  condition: AgeCondition,
  age: number | null | undefined
): boolean {
  if (age === null || age === undefined || Number.isNaN(age)) return false;
  const { gte, gt, lt, lte } = condition;
  if (gte !== undefined && !(age >= gte)) return false;
  if (gt !== undefined && !(age > gt)) return false;
  if (lt !== undefined && !(age < lt)) return false;
  if (lte !== undefined && !(age <= lte)) return false;
  return true;
}

function ruleMatches(
  rule: RangeRule,
  sexKey: string | null,
  age: number | null | undefined,
  ignoreAge: boolean
): boolean {
  const when = rule.when ?? {};
  if (typeof when !== 'object' || Array.isArray(when)) return false;
  if (Object.keys(when).some((key) => !WHEN_KEYS.has(key))) return false;
  if (when.sex !== undefined && when.sex !== sexKey) return false;
  if (when.age !== undefined) {
    if (!isValidAgeCondition(when.age)) return false;
    if (!ignoreAge && !ageMatches(when.age, age)) return false;
  }
  return true;
}

export interface ResolvedRange {
  bands: SeverityRanges | null | undefined;
  /** Set when no rule covered the patient's age and the age conditions were
   *  dropped: the `when.age` of the rule that was used instead. */
  ageFallback: AgeCondition | null;
}

const NO_RANGE: ResolvedRange = { bands: null, ageFallback: null };

/**
 * The band set for this patient, plus whether the age fallback was needed.
 * Rule lists: first strict match wins; failing that, the first rule for the
 * sex with its age condition ignored. Legacy blobs: the sex bucket, nothing
 * without a sex.
 */
export function resolveRange(
  referenceRanges: ReferenceRanges | null | undefined,
  sex: string | null | undefined,
  age?: number | null
): ResolvedRange {
  if (!referenceRanges || typeof referenceRanges !== 'object') return NO_RANGE;
  const sexKey = normalizeSex(sex);
  if (isRuleList(referenceRanges)) {
    const rules = referenceRanges.rules.filter(
      (r): r is RangeRule => !!r && typeof r === 'object'
    );
    const strict = rules.find((r) => ruleMatches(r, sexKey, age, false));
    if (strict) return { bands: strict.bands, ageFallback: null };
    const relaxed = rules.find((r) => ruleMatches(r, sexKey, age, true));
    if (!relaxed) return NO_RANGE;
    return { bands: relaxed.bands, ageFallback: relaxed.when?.age ?? null };
  }
  if (!sexKey) return NO_RANGE;
  return { bands: referenceRanges[sexKey], ageFallback: null };
}

/** `resolveRange(...).bands` for callers that only grade or format. */
export function resolveSeverityRanges(
  referenceRanges: ReferenceRanges | null | undefined,
  sex: string | null | undefined,
  age?: number | null
): SeverityRanges | null | undefined {
  return resolveRange(referenceRanges, sex, age).bands;
}

/** "20–39", "60+", "under 40" — the age block a condition describes. */
export function describeAgeCondition(condition: AgeCondition): string {
  const low =
    condition.gte ??
    (condition.gt !== undefined ? condition.gt + 1 : undefined);
  const high =
    condition.lte ??
    (condition.lt !== undefined ? condition.lt - 1 : undefined);
  if (low !== undefined && high !== undefined) return `${low}–${high}`;
  if (low !== undefined) return `${low}+`;
  if (high !== undefined) return `up to ${high}`;
  return 'any age';
}
